import React, { useMemo, useRef, useState } from "react";
import "@/components/sections/Faq.css";
import { eventConfig } from "@/config/eventConfig";
import { FaqItem, CATEGORY_ORDER } from "@/components/sections/FaqItem";
import { FaqSearch, FaqFilters, FaqNoResults, FaqIntro, FaqBackground } from "@/components/sections/FaqControls";
import { usePointerSpotlight } from "@/hooks/usePointerSpotlight";
import { useRevealOnce } from "@/hooks/useStaggeredReveal";

const pad2 = (n) => String(n).padStart(2, "0");
const SPOTLIGHT = { xVar: "--cfl-x", yVar: "--cfl-y", restX: "68%", restY: "42%", respectReducedMotion: true };
const REVEAL = { threshold: 0.16, afterMs: 120 };

// FAQ list is static config; number + default category are derived once.
const FAQ = eventConfig.faq.map((item, i) => ({ ...item, number: pad2(i + 1), category: item.category || "registration" }));
const CATEGORIES = ["all", ...CATEGORY_ORDER.filter((c) => FAQ.some((f) => f.category === c))];

const matches = (item, category, q) => {
  const categoryMatch = category === "all" || item.category === category;
  const searchMatch = !q || `${item.q} ${item.a}`.toLowerCase().includes(q);
  return categoryMatch && searchMatch;
};

const Faq = () => {
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openIndex, setOpenIndex] = useState(0);

  const [introVisible, panelVisible] = useRevealOnce(rootRef, REVEAL);
  const spotlight = usePointerSpotlight(rootRef, SPOTLIGHT);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => new Set(FAQ.filter((item) => matches(item, activeCategory, q)).map((i) => i.q)), [activeCategory, q]);
  const visibleCount = visible.size;

  const toggleItem = (idx) => setOpenIndex((cur) => (cur === idx ? -1 : idx));
  const clearSearch = () => {
    setQuery("");
    searchRef.current?.focus();
  };
  const resetAll = () => {
    setActiveCategory("all");
    clearSearch();
  };

  return (
    <section
      ref={rootRef}
      className="cpf-faq-live has-js"
      id="faq"
      aria-labelledby="cpf-faq-title"
      data-testid="faq-section"
      onPointerMove={spotlight.onPointerMove}
      onPointerLeave={spotlight.onPointerLeave}
    >
      <FaqBackground />

      <div className="cfl-container">
        <div className="cfl-grid">
          <FaqIntro visible={introVisible} total={FAQ.length} />

          <div className={`cfl-faq-panel${panelVisible ? " is-visible" : ""}`}>
            <FaqSearch query={query} onChange={setQuery} onClear={clearSearch} inputRef={searchRef} />
            <FaqFilters categories={CATEGORIES} active={activeCategory} onSelect={setActiveCategory} visibleCount={visibleCount} />

            <div className="cfl-faq-list" data-testid="faq-accordion">
              {FAQ.map((item, i) => {
                const show = visible.has(item.q);
                return (
                  <FaqItem key={item.q} item={item} index={i} show={show} isOpen={openIndex === i && show} onToggle={() => toggleItem(i)} />
                );
              })}
            </div>

            {visibleCount === 0 && <FaqNoResults onReset={resetAll} />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faq;
